import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Divider,
  Form,
  InlineAlert,
  PageHeading,
  CardFooterPrompt,
} from '@ai-tutor/ui';
import { Mail } from 'lucide-react';

import { DeactivatedAccountCard } from '@/components/features/account/DeactivatedAccountCard';
import { XoneraSpinner } from '@/components/core/common/feedback/XoneraSpinner';
import {
  loginSchema,
  registerSchema,
  resetPasswordSchema,
  AuthMode,
} from '@/lib/schemas/auth';

import { AuthFormFields } from './AuthFormFields';
import { GoogleSignInButton } from './GoogleSignInButton';
import { useAuthSubmit, type AuthFormValues } from './useAuthSubmit';

const COPY: Record<AuthMode, { title: string; sub: string; cta: string }> = {
  login: {
    title: 'Welcome back',
    sub: 'Sign in to pick up where you left off.',
    cta: 'Sign in',
  },
  register: {
    title: 'Create your account',
    sub: 'Free to start. No card needed.',
    cta: 'Create account',
  },
  reset: {
    title: 'Reset your password',
    sub: 'We will email you a link to set a new one.',
    cta: 'Send reset link',
  },
};

const SCHEMA = {
  login: loginSchema,
  register: registerSchema,
  reset: resetPasswordSchema,
} as const;

const DEFAULTS = {
  login: { email: '', password: '' },
  register: { fullName: '', email: '', password: '' },
  reset: { email: '' },
} as const;

/**
 * Magic link, as a real full-width button rather than a tucked-away text link.
 *
 * Even with Google beside it this stays a real button: three methods in all
 * (password, Google, email link) is inside the "surface 2-3 and tuck the rest
 * away" guidance, and the email link is the way back in for anyone who signed
 * up with a password they no longer remember.
 */
function MagicLinkAction({
  pending,
  disabled,
  onRequest,
}: {
  pending: boolean;
  disabled: boolean;
  onRequest: () => void;
}) {
  return (
    <>
      <Divider label='Or' />
      <Button
        type='button'
        variant='outline'
        size='touch'
        fullWidth
        onClick={onRequest}
        disabled={disabled}
      >
        {pending ? (
          <XoneraSpinner className='mr-2 h-4 w-4' label='' />
        ) : (
          <Mail className='mr-2 h-4 w-4' />
        )}
        Email me a sign-in link
      </Button>
    </>
  );
}

interface AuthFormProps {
  mode: AuthMode;
  /** Pre-fills the email field. See `initialEmail` handling below. */
  initialEmail?: string;
  onStateChange: (state: string, email?: string) => void;
  /** Second argument carries the typed email across the swap - see `swapMode`. */
  onModeChange: (mode: AuthMode, email?: string) => void;
}

/**
 * Sign in, sign up and request-a-reset, as one component in three modes.
 *
 * Rebuilt against current login/signup guidance. What changed:
 *
 * - **`autoComplete` and `inputMode` on every field.** The previous version set
 *   neither, anywhere, so password managers and mobile autofill did not work on
 *   the sign-in screen at all.
 * - **A password reveal toggle**, which is what lets sign-up drop its
 *   confirm-password field: you verify by looking, not by typing it twice.
 * - **"Forgot your password?" moved onto the form**, directly under the
 *   password field, rather than sitting in the card footer below the sign-up
 *   prompt.
 * - **The deactivated branch is its own screen** - see
 *   `DeactivatedAccountCard`.
 * - **Separate pending flags** so "Sign in" no longer spins the magic-link
 *   button too.
 * - **Register branches on `data.session`** - see `useAuthSubmit`.
 *
 * Google sits ABOVE the email form in sign-in and sign-up (one tap beats
 * typing, so it leads). It is the same action in
 * both modes - the first visit creates the account - and is absent from reset,
 * which is about a password Google users do not have.
 */
export function AuthForm({
  mode,
  initialEmail,
  onStateChange,
  onModeChange,
}: AuthFormProps) {
  const {
    isSubmitting,
    isMagicLinkPending,
    error,
    errorAction,
    bannedEmail,
    setBannedEmail,
    onSubmit,
    requestMagicLink,
  } = useAuthSubmit({ mode, onStateChange });

  const form = useForm<AuthFormValues>({
    resolver: zodResolver(SCHEMA[mode]),
    // `AuthPage` gives this component a `key={mode}`, so a mode change
    // REMOUNTS it and these defaults are re-applied per mode. That is what
    // clears a stale form-level error and stale field values on the swap;
    // `useForm` only reads `defaultValues` on mount, so without the remount
    // switching into `register` left `fullName` as `undefined` rather than ''.
    defaultValues: {
      ...DEFAULTS[mode],
      ...(initialEmail ? { email: initialEmail } : {}),
    } as AuthFormValues,
  });

  const copy = COPY[mode];

  /**
   * Every mode swap goes through here so the email survives it.
   *
   * The remount above resets the form, which would otherwise discard an
   * address someone has already typed - and "Forgot your password?" is exactly
   * the moment you least want to retype it. Before the remount this worked by
   * accident, because one long-lived `useForm` simply kept its values.
   */
  const swapMode = (next: AuthMode) =>
    onModeChange(next, form.getValues('email'));

  if (bannedEmail) {
    return (
      <DeactivatedAccountCard
        email={bannedEmail}
        onUseDifferentAccount={() => setBannedEmail(null)}
      />
    );
  }

  return (
    <div className='space-y-6'>
      <PageHeading title={copy.title} subtitle={copy.sub} />

      {/* Action from the classifier, not the mode - see `lib/auth/auth-error`. */}
      {error && (
        <InlineAlert
          message={error}
          actionLabel={errorAction?.label}
          onAction={errorAction ? () => swapMode(errorAction.mode) : undefined}
        />
      )}

      <GoogleSignInButton mode={mode} disabled={isSubmitting} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
          <AuthFormFields
            mode={mode}
            control={form.control}
            onForgotPassword={() => swapMode('reset')}
          />

          <Button type='submit' size='touch' fullWidth disabled={isSubmitting}>
            {isSubmitting && (
              <XoneraSpinner className='mr-2 h-4 w-4' label='' />
            )}
            {copy.cta}
          </Button>
        </form>
      </Form>

      {mode === 'login' && (
        <MagicLinkAction
          pending={isMagicLinkPending}
          disabled={isSubmitting || isMagicLinkPending}
          onRequest={() => requestMagicLink(form.getValues('email'))}
        />
      )}

      <CardFooterPrompt
        prompt={
          mode === 'login' ? 'New to Xonera?' : 'Already have an account?'
        }
        actionLabel={mode === 'login' ? 'Create an account' : 'Sign in'}
        onAction={() => swapMode(mode === 'login' ? 'register' : 'login')}
      />
    </div>
  );
}
